/**
 * EraPack theme: the header, the search overlay and the footer newsletter.
 *
 * Ports the state and handlers of components/shell/SiteNav.tsx,
 * SearchOverlay.tsx and NewsletterForm.tsx onto the markup the sections
 * render. Nothing here touches the cart itself: the cart button asks
 * cart.js to open the drawer (the `erapack:cart:open` event, or
 * `window.EraPack.openCart`) and falls back to the cart page.
 */

const EP = window.EraPack || {};
const trapFocus = EP.trapFocus || (() => () => {});
const lockScroll = EP.lockScroll || ((on = true) => {
  document.body.style.overflow = on ? "hidden" : "";
});

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/* ── Mobile full-screen sheet ──────────────────────────────────────────── */

function initMobileMenu(header) {
  const sheet = header.querySelector("[data-mobile-menu]");
  const openBtn = header.querySelector("[data-menu-open]");
  const closeBtn = sheet && sheet.querySelector("[data-menu-close]");
  if (!sheet || !openBtn) return null;

  let release = null;
  let open = false;

  const setOpen = (next) => {
    if (next === open) return;
    open = next;
    sheet.hidden = !open;
    openBtn.setAttribute("aria-expanded", String(open));
    lockScroll(open);
    if (open) {
      // The sheet is a dialog: focus lands on its close button, Tab stays
      // inside, and the burger gets focus back when it closes.
      release = trapFocus(sheet, closeBtn || undefined);
    } else if (release) {
      release();
      release = null;
    }
  };

  openBtn.addEventListener("click", () => setOpen(true));
  if (closeBtn) closeBtn.addEventListener("click", () => setOpen(false));
  // Links inside the sheet navigate; close first so the scroll lock lifts
  // if the browser restores this page from its cache.
  sheet.addEventListener("click", (e) => {
    const a = e.target.closest("a[href]");
    if (a) setOpen(false);
  });

  return { isOpen: () => open, close: () => setOpen(false), open: () => setOpen(true) };
}

/* ── Shop Now menu: the photo of whichever size the pointer is on ──────── */

function initShopMenu(header) {
  const menu = header.querySelector("[data-shop-menu]");
  if (!menu) return;
  const photos = Array.from(menu.querySelectorAll("[data-shop-photo]"));
  const links = Array.from(menu.querySelectorAll("[data-shop-size]"));
  if (photos.length === 0 || links.length === 0) return;
  const first = links[0].dataset.shopSize;

  // The photographs ship as data-src (sections/site-nav.liquid), so a page
  // that never opens the menu downloads none of them. The first hover, focus
  // or touch on the Shop Now item (which is what opens the menu) loads them
  // all, the one on show first.
  const item = menu.closest(".new-menu-li-0") || menu;
  let loaded = false;
  const loadPhotos = () => {
    if (loaded) return;
    loaded = true;
    const shown = photos.filter((img) => img.className === "shop-menu-photois-on");
    for (const img of shown.concat(photos.filter((img) => !shown.includes(img)))) {
      if (img.dataset.src && !img.getAttribute("src")) img.src = img.dataset.src;
    }
    for (const t of ["pointerenter", "focusin", "touchstart"]) item.removeEventListener(t, loadPhotos);
  };
  for (const t of ["pointerenter", "focusin", "touchstart"]) item.addEventListener(t, loadPhotos, { passive: true });
  // Already open (the pointer was on it while the script loaded).
  if (item.matches(":hover, :focus-within")) loadPhotos();

  /**
   * Null shows the smallest. The photo on show gets the class the site gives
   * it, `shop-menu-photois-on` (no space, so no rule: it sits in the flow at
   * its own size); the rest are the absolute, transparent `shop-menu-photo`.
   * See the comment in sections/site-nav.liquid.
   */
  const peek = (handle) => {
    const on = handle || first;
    for (const img of photos) img.className = img.dataset.shopPhoto === on ? "shop-menu-photois-on" : "shop-menu-photo";
  };

  for (const a of links) {
    a.addEventListener("mouseenter", () => peek(a.dataset.shopSize));
    a.addEventListener("focus", () => peek(a.dataset.shopSize));
  }

  // Groups (Paper cups / Cold cups): the pointer or focus on a group shows
  // its sizes and the photo of its first size; leaving the menu goes back
  // to Paper cups.
  const cats = Array.from(menu.querySelectorAll("[data-shop-cat]"));
  const panels = Array.from(menu.querySelectorAll("[data-shop-cat-panel]"));
  const showCat = (key) => {
    if (!cats.length) return;
    for (const c of cats) c.classList.toggle("shop-menu-cat--on", c.dataset.shopCat === key);
    let firstSize = null;
    for (const panel of panels) {
      const on = panel.dataset.shopCatPanel === key;
      panel.hidden = !on;
      if (on) {
        const l = panel.querySelector("[data-shop-size]");
        firstSize = l ? l.dataset.shopSize : null;
      }
    }
    peek(firstSize);
  };

  // Hover intent: a pointer heading right, towards the sizes column, may
  // cross another group on the way (Greaseproof paper to its first size
  // crosses Cold cups), so a group it enters while moving right only takes
  // over if the pointer is still on it a moment later. Straight up or down
  // the list switches at once.
  let lastX = 0;
  let lastY = 0;
  let dx = 0;
  let pending = 0;
  const cancel = () => {
    window.clearTimeout(pending);
    pending = 0;
  };
  menu.addEventListener("mousemove", (e) => {
    dx = e.clientX - lastX;
    if (Math.abs(e.clientX - lastX) + Math.abs(e.clientY - lastY) > 0) {
      lastX = e.clientX;
      lastY = e.clientY;
    }
  });
  for (const c of cats) {
    c.addEventListener("mouseenter", () => {
      cancel();
      if (c.classList.contains("shop-menu-cat--on")) return;
      if (dx > 0) pending = window.setTimeout(() => showCat(c.dataset.shopCat), 250);
      else showCat(c.dataset.shopCat);
    });
    c.addEventListener("mouseleave", cancel);
    c.addEventListener("focus", () => {
      cancel();
      showCat(c.dataset.shopCat);
    });
  }
  for (const panel of panels) panel.addEventListener("mouseenter", cancel);
  menu.addEventListener("mouseleave", () => {
    cancel();
    if (cats.length) showCat(cats[0].dataset.shopCat);
    else peek(null);
  });

  // Keyboard: the groups come before their size lists in the markup, and
  // focusing a group hides the other lists, so plain Tab never reached the
  // paper or cold sizes. Tab walks group, its sizes, next group, its sizes;
  // Shift+Tab walks back the same way.
  const order = [];
  for (const c of cats) {
    order.push({ el: c, cat: c.dataset.shopCat });
    const panel = panels.find((p) => p.dataset.shopCatPanel === c.dataset.shopCat);
    if (panel) for (const a of panel.querySelectorAll("a[href]")) order.push({ el: a, cat: c.dataset.shopCat });
  }
  menu.addEventListener("keydown", (e) => {
    if (e.key !== "Tab" || e.altKey || e.ctrlKey || e.metaKey || !order.length) return;
    const i = order.findIndex((o) => o.el === document.activeElement);
    if (i === -1) return;
    const next = order[i + (e.shiftKey ? -1 : 1)];
    // Past either end: the browser's own order (Shop Now before, Cups for
    // events after) is already right.
    if (!next) return;
    e.preventDefault();
    cancel();
    showCat(next.cat);
    next.el.focus();
  });

  if (cats.length) showCat(cats[0].dataset.shopCat);
  else peek(null);
}

/* ── Desktop dropdowns (Shop Now, About us): Escape closes ────────────── */

// The dropdowns open on :hover and :focus-within (site.css), so Escape has
// to shut one while focus is still inside: `is-closed` hides it (and takes
// its links out of the Tab order) and focus goes back to the top link. It
// opens again once the pointer comes back or focus leaves and returns.
function initDropdowns(header) {
  for (const li of header.querySelectorAll(".new-menu-li-0")) {
    const sub = li.querySelector(".new-menu-ul-1");
    const top = li.querySelector(".new-menu-a-0");
    if (!sub || !top) continue;
    li.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || li.classList.contains("is-closed")) return;
      li.classList.add("is-closed");
      top.focus();
    });
    li.addEventListener("mouseenter", () => li.classList.remove("is-closed"));
    li.addEventListener("focusout", (e) => {
      if (!e.relatedTarget || !li.contains(e.relatedTarget)) li.classList.remove("is-closed");
    });
  }
}

/* ── Cart button and badge ─────────────────────────────────────────────── */

// cart.js announces every change with `detail.count` (cart lines that are
// not the artwork-setup fee, as the site's cartCount() counted). A cart
// JSON in `detail` is counted the same way; never cart.item_count, which
// is cups, not lines. Bound once; it updates whichever header is current.
let cartSetCount = null;
const onCart = (e) => {
  if (!cartSetCount) return;
  const d = (e && e.detail) || {};
  if (typeof d.count === "number") return cartSetCount(d.count);
  const cart = d.cart || d;
  if (cart && Array.isArray(cart.items)) {
    cartSetCount(cart.items.filter((i) => i.handle !== "artwork-setup" && i.product_handle !== "artwork-setup").length);
  }
};
document.addEventListener("erapack:cart:updated", onCart);
document.addEventListener("cart:updated", onCart);

function initCartButton(header) {
  const btn = header.querySelector("[data-cart-open]");
  const badge = header.querySelector("[data-cart-badge]");
  const paren = header.querySelector("[data-cart-count-paren]");

  const setCount = (count) => {
    const n = Math.max(0, Number(count) || 0);
    if (badge) {
      badge.textContent = String(n);
      badge.hidden = n === 0;
    }
    if (paren) {
      paren.textContent = ` (${n})`;
      paren.hidden = n === 0;
    }
    if (btn) {
      btn.dataset.cartCount = String(n);
      btn.setAttribute("aria-label", `Cart, ${n} item${n === 1 ? "" : "s"}`);
    }
  };

  cartSetCount = setCount;

  if (btn) {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const ask = new CustomEvent("erapack:cart:open", {
        bubbles: true,
        cancelable: true,
        detail: { trigger: btn },
      });
      const notHandled = document.dispatchEvent(ask);
      if (!notHandled) return; // cart.js took it
      if (typeof window.EraPack?.openCart === "function") {
        window.EraPack.openCart(btn);
        return;
      }
      window.location.assign(btn.dataset.cartUrl || "/cart");
    });
  }

  return { setCount };
}

/* ── Search overlay ────────────────────────────────────────────────────── */

const searchOpeners = () => Array.from(document.querySelectorAll("[data-search-open]"));

function initSearch(overlay) {
  if (!overlay) return null;
  const input = overlay.querySelector("[data-search-input]");
  const form = overlay.querySelector("[data-search-form]");
  const list = overlay.querySelector("[data-search-results]");
  const entries = Array.from(overlay.querySelectorAll("[data-search-entry]"));
  const empty = overlay.querySelector("[data-search-empty]");
  const term = overlay.querySelector("[data-search-term]");
  const closers = Array.from(overlay.querySelectorAll("[data-search-close]"));

  // The haystack per entry: label, sub and keywords, lower-cased once.
  const index = entries.map((li) => ({
    li,
    href: li.dataset.href,
    text: ` ${li.textContent} ${li.dataset.keywords || ""}`.replace(/[·×,.()/-]/g, " ").replace(/\s+/g, " ").toLowerCase(),
  }));

  let open = false;
  let release = null;
  let focusTimer = 0;

  // Every word typed has to start a word of the entry, in any order
  // ("cups 8oz" and "greaseproof 160" find their product; "proof" is not
  // "greaseproof").
  const filter = () => {
    const q = (input ? input.value : "").trim().toLowerCase();
    const words = q.split(/\s+/).filter((w) => /[a-z0-9]/.test(w));
    let shown = 0;
    for (const e of index) {
      const on = !words.length || words.every((w) => e.text.includes(` ${w}`));
      e.li.hidden = !on;
      if (on) shown += 1;
    }
    if (empty) {
      if (term) term.textContent = input ? input.value : "";
      empty.hidden = shown !== 0;
    }
    return shown;
  };

  const firstResult = () => index.find((e) => !e.li.hidden);

  const setOpen = (next) => {
    if (next === open) return;
    open = next;
    overlay.hidden = !open;
    for (const b of searchOpeners()) b.setAttribute("aria-expanded", String(open));
    lockScroll(open);
    if (open) {
      if (input) input.value = "";
      filter();
      release = trapFocus(overlay, input || undefined);
      // Focus the field on open (the trap's own move may land on it first).
      focusTimer = window.setTimeout(() => input && input.focus(), 30);
    } else {
      window.clearTimeout(focusTimer);
      if (input) input.value = "";
      filter();
      if (release) {
        release();
        release = null;
      }
      // The site unmounts the overlay and leaves focus on the page, so no
      // focus ring comes back on the Search button: undo the trap's restore.
      const a = document.activeElement;
      if (a && a !== document.body && a.closest("[data-search-open]")) a.blur();
    }
  };

  const go = (href) => {
    if (!href) return;
    setOpen(false);
    window.location.assign(href);
  };

  for (const b of closers) b.addEventListener("click", () => setOpen(false));
  if (input) input.addEventListener("input", filter);
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const hit = firstResult();
      if (hit) return go(hit.href);
      // Nothing here matches: hand the words to the search page.
      const q = input ? input.value.trim() : "";
      if (q) go(`${form.getAttribute("action") || "/search"}?q=${encodeURIComponent(q)}`);
    });
  }
  if (list) {
    list.addEventListener("click", (e) => {
      const b = e.target.closest("[data-search-go]");
      if (b) go(b.dataset.searchGo);
    });
  }

  return { isOpen: () => open, close: () => setOpen(false), open: () => setOpen(true) };
}

/* ── Footer newsletter: check the address before it posts ──────────────── */

// Shopify's bot protection (hCaptcha, added through content_for_header)
// listens for the same submit and opened its challenge even for an address
// we had already rejected. This check runs first, on window in the capture
// phase, and a bad address stops the event there, so nothing else sees it.
function initNewsletter(form) {
  const email = form.querySelector("[data-newsletter-email]");
  const error = form.querySelector("[data-newsletter-error]");
  if (!email) return;
  newsletterChecks.set(form, (e) => {
    const address = email.value.trim();
    if (EMAIL.test(address)) {
      email.value = address;
      email.removeAttribute("aria-invalid");
      if (error) error.hidden = true;
      return; // Shopify takes it from here
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    if (error) {
      error.textContent = "That email doesn't look right.";
      error.hidden = false;
    }
    // The site leaves focus where it was (on the button); the role="alert"
    // line is what announces the problem.
    email.setAttribute("aria-invalid", "true");
  });
  // As on the site, the message stays until the next submit.
}

const newsletterChecks = new WeakMap();
window.addEventListener(
  "submit",
  (e) => {
    const check = e.target instanceof HTMLFormElement && newsletterChecks.get(e.target);
    if (check) check(e);
  },
  true,
);

/* ── Wire up ───────────────────────────────────────────────────────────── */

// Each node is bound once (data-ep-bound); init() runs again when the theme
// editor re-renders a section, so the new nodes get their behaviour.
const claim = (el) => {
  if (!el || el.dataset.epBound) return false;
  el.dataset.epBound = "1";
  return true;
};

let menu = null;
let search = null;

function init() {
  const header = document.querySelector("[data-site-nav]");
  if (claim(header)) {
    if (menu && menu.isOpen()) menu.close();
    menu = initMobileMenu(header);
    initShopMenu(header);
    initDropdowns(header);
    initCartButton(header);
  }
  const overlay = document.querySelector("[data-search-overlay]");
  if (claim(overlay)) {
    if (search && search.isOpen()) search.close();
    search = initSearch(overlay);
  }
  for (const b of searchOpeners()) {
    if (claim(b)) b.addEventListener("click", () => search && search.open());
  }
  for (const form of document.querySelectorAll("[data-newsletter-form]")) {
    if (claim(form)) initNewsletter(form);
  }

  window.EraPack = Object.assign(window.EraPack || {}, {
    nav: { menu, search },
    openSearch: () => search && search.open(),
    closeSearch: () => search && search.close(),
  });
}

// Esc closes whichever of ours is open.
window.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  if (search && search.isOpen()) search.close();
  if (menu && menu.isOpen()) menu.close();
});

// A page restored from the back-forward cache keeps its DOM: make sure
// nothing of ours is still holding the scroll lock.
window.addEventListener("pageshow", (e) => {
  if (!e.persisted) return;
  if (search && search.isOpen()) search.close();
  if (menu && menu.isOpen()) menu.close();
});

// Theme editor: a re-rendered header, search or footer section is new DOM.
document.addEventListener("shopify:section:load", (e) => {
  const t = e.target;
  if (t && t.querySelector && t.querySelector("[data-site-nav],[data-search-overlay],[data-search-open],[data-newsletter-form]")) init();
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
